import './App.css'

import {
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  RefreshIcon,
  ShareIcon,
} from '@heroicons/react/outline'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AlertContainer } from './components/alerts/AlertContainer'
import { PuzzleBoard } from './components/game/PuzzleBoard'
import { InfoModal } from './components/modals/InfoModal'
import { ProgressModal } from './components/modals/ProgressModal'
import { SOLUTIONS } from './constants/solns'
import { CURATED_SOLUTION_INDEXES_V1 } from './constants/solutionCatalog'
import { useAlert } from './context/AlertContext'
import { copyTextToClipboard } from './lib/clipboard'
import {
  GAME_STORAGE_VERSION,
  GameMode,
  SavedGameState,
  clearGameState,
  loadCurrentPractice,
  loadGameState,
  loadPreferredMode,
  saveCurrentPractice,
  saveGameState,
  savePreferredMode,
} from './lib/gameStorage'
import {
  loadPlayerStats,
  recordPuzzleCompletion,
  savePlayerStats,
} from './lib/playerStats'
import {
  CellPosition,
  applyKnownCells,
  cellKey,
  chooseHintCells,
  choosePracticePuzzleIndexFromPool,
  createClueCells,
  createEmptyBoard,
  getEditableCellsForRow,
} from './lib/puzzle'
import { CharStatus, getGuessStatuses } from './lib/statuses'
import {
  solution as dailySolution,
  solutionCatalogIndex as dailySolutionCatalogIndex,
  solutionIndex as dailySolutionIndex,
  isWordInWordList,
  localeAwareUpperCase,
} from './lib/words'

const SIZE = 5
const RECENT_PRACTICE_KEY = 'word-square-puzzle:recent-practice'
type RuntimeGame = SavedGameState & {
  solution: string[]
}

const upperSolution = (index: number) =>
  SOLUTIONS[index].map((word) => word.toUpperCase())

const getSolvedIndexes = (rows: readonly boolean[]) =>
  rows.reduce<number[]>((indexes, solved, index) => {
    if (solved) indexes.push(index)
    return indexes
  }, [])

const dailyPuzzleId = `daily-${dailySolutionIndex}`
const loadedDateKey = getLocalDateKey(new Date())

function getLocalDateKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
}

const getRecentPractice = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_PRACTICE_KEY) || '[]')
    return Array.isArray(parsed)
      ? parsed
          .filter((value): value is number => Number.isInteger(value))
          .slice(-12)
      : []
  } catch {
    return []
  }
}

const rememberPractice = (index: number) => {
  try {
    const recent = getRecentPractice().filter((value) => value !== index)
    localStorage.setItem(
      RECENT_PRACTICE_KEY,
      JSON.stringify([...recent, index].slice(-12))
    )
  } catch {
    // Storage can be unavailable in privacy-focused browsers.
  }
}

const createGame = (
  mode: GameMode,
  solutionIndex: number,
  puzzleId: string
): RuntimeGame => {
  const solution =
    mode === 'daily'
      ? dailySolution.map((word) => word.toUpperCase())
      : upperSolution(solutionIndex)
  const clueCells = createClueCells(solution, puzzleId)
  const now = new Date().toISOString()
  const board = applyKnownCells(
    createEmptyBoard(SIZE),
    solution,
    clueCells,
    new Set(),
    new Set<number>()
  )

  return {
    version: GAME_STORAGE_VERSION,
    puzzleId,
    solutionIndex,
    solutionSignature: solution.join('|'),
    mode,
    solution,
    board,
    solvedRows: Array(SIZE).fill(false),
    clueCells: Array.from(clueCells),
    revealedCells: [],
    attempts: Array(SIZE).fill(0),
    lastAttempts: Array(SIZE).fill(null),
    mistakes: 0,
    hintsUsed: 0,
    startedAt: now,
    updatedAt: now,
    completedAt: null,
    statsRecorded: false,
  }
}

const restoreGame = (
  mode: GameMode,
  solutionIndex: number,
  puzzleId: string
): RuntimeGame => {
  const saved = loadGameState(puzzleId)
  const solution =
    mode === 'daily'
      ? dailySolution.map((word) => word.toUpperCase())
      : upperSolution(solutionIndex)

  if (
    !saved ||
    saved.solutionIndex !== solutionIndex ||
    saved.solutionSignature !== solution.join('|') ||
    saved.mode !== mode
  ) {
    return createGame(mode, solutionIndex, puzzleId)
  }

  return {
    ...saved,
    solution,
    board: applyKnownCells(
      saved.board,
      solution,
      new Set(saved.clueCells),
      new Set(saved.revealedCells),
      getSolvedIndexes(saved.solvedRows)
    ),
  }
}

const getInitialGame = (): RuntimeGame => {
  const preferred = loadPreferredMode()
  const practice = loadCurrentPractice()
  const practiceMatchesDaily =
    practice?.solutionIndex === dailySolutionCatalogIndex
  if (
    preferred === 'practice' &&
    practice &&
    !practiceMatchesDaily &&
    practice.solutionIndex >= 0 &&
    practice.solutionIndex < SOLUTIONS.length
  ) {
    return restoreGame('practice', practice.solutionIndex, practice.puzzleId)
  }
  return restoreGame('daily', dailySolutionIndex, dailyPuzzleId)
}

const getFirstSelectableCell = (game: RuntimeGame): CellPosition => {
  const firstUnsolved = game.solvedRows.findIndex((solved) => !solved)
  if (firstUnsolved < 0) return { row: 0, col: 0 }
  const editable = getEditableCellsForRow(
    firstUnsolved,
    SIZE,
    new Set(game.clueCells),
    new Set(game.revealedCells),
    getSolvedIndexes(game.solvedRows)
  )
  return editable[0] || { row: firstUnsolved, col: 0 }
}

const isCellEditable = (game: RuntimeGame, row: number, col: number) =>
  !game.solvedRows[row] &&
  !game.solvedRows[col] &&
  !game.clueCells.includes(cellKey({ row, col })) &&
  !game.revealedCells.includes(cellKey({ row, col }))

const feedbackForGame = (game: RuntimeGame) =>
  Array.from({ length: SIZE }, (_, row) => {
    const attempt = game.lastAttempts[row]
    if (!attempt || game.solvedRows[row]) return Array(SIZE).fill(undefined)
    return getGuessStatuses(game.solution[row], attempt)
  }) as (CharStatus | undefined)[][]

const formatElapsed = (startedAt: string, completedAt: string | null) => {
  const end = completedAt ? Date.parse(completedAt) : Date.now()
  const seconds = Math.max(0, Math.floor((end - Date.parse(startedAt)) / 1000))
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

function App() {
  const { showError, showSuccess } = useAlert()
  const initialGame = useRef<RuntimeGame | null>(null)
  if (!initialGame.current) initialGame.current = getInitialGame()
  const [game, setGame] = useState<RuntimeGame>(initialGame.current)
  const [selectedCell, setSelectedCell] = useState(() =>
    getFirstSelectableCell(initialGame.current as RuntimeGame)
  )
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const [isStatsOpen, setIsStatsOpen] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [isSaveAvailable, setIsSaveAvailable] = useState(true)
  const announcedPuzzleRef = useRef<string | null>(
    initialGame.current.completedAt ? initialGame.current.puzzleId : null
  )
  const [playerStats, setPlayerStats] = useState(loadPlayerStats)
  const gameRef = useRef(game)
  const selectedCellButtonRef = useRef<HTMLButtonElement>(null)
  const nativeInputRef = useRef<HTMLInputElement>(null)
  gameRef.current = game

  const clueCells = useMemo(() => new Set(game.clueCells), [game.clueCells])
  const revealedCells = useMemo(
    () => new Set(game.revealedCells),
    [game.revealedCells]
  )
  const feedback = useMemo(() => feedbackForGame(game), [game])
  const solvedCount = game.solvedRows.filter(Boolean).length
  const isComplete = solvedCount === SIZE
  const activeRow = game.solvedRows[selectedCell.row]
    ? game.solvedRows.findIndex((solved) => !solved)
    : selectedCell.row

  const focusNativeInput = useCallback(() => {
    const input = nativeInputRef.current
    if (!input || input.readOnly) return
    try {
      input.focus({ preventScroll: true })
    } catch {
      input.focus()
    }
  }, [])

  useEffect(() => {
    const { solution: _solution, ...state } = game
    setIsSaveAvailable(
      saveGameState({ ...state, updatedAt: new Date().toISOString() })
    )
  }, [game])

  useEffect(() => {
    savePreferredMode(game.mode)
    if (game.mode === 'practice') {
      saveCurrentPractice({
        puzzleId: game.puzzleId,
        solutionIndex: game.solutionIndex,
      })
    }
  }, [game.mode, game.puzzleId, game.solutionIndex])

  useEffect(() => {
    let shouldShow = false
    try {
      shouldShow = !localStorage.getItem('word-square-puzzle:onboarded')
      if (shouldShow) localStorage.setItem('word-square-puzzle:onboarded', '1')
    } catch {
      shouldShow = true
    }

    if (shouldShow) {
      const timeout = window.setTimeout(() => setIsInfoOpen(true), 450)
      return () => window.clearTimeout(timeout)
    }
  }, [])

  useEffect(() => {
    const refreshForNewDay = () => {
      if (getLocalDateKey(new Date()) !== loadedDateKey) {
        window.location.reload()
      }
    }
    const interval = window.setInterval(refreshForNewDay, 60_000)
    document.addEventListener('visibilitychange', refreshForNewDay)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', refreshForNewDay)
    }
  }, [])

  useEffect(() => {
    if (isComplete && announcedPuzzleRef.current !== game.puzzleId) {
      announcedPuzzleRef.current = game.puzzleId
      showSuccess('Board complete!', { durationMs: 3600 })
      const timeout = window.setTimeout(() => setIsStatsOpen(true), 650)
      return () => window.clearTimeout(timeout)
    }
  }, [game.puzzleId, isComplete, showSuccess])

  useEffect(() => {
    if (isComplete || isInfoOpen || isStatsOpen) {
      nativeInputRef.current?.blur()
    }
  }, [isComplete, isInfoOpen, isStatsOpen])

  useEffect(() => {
    if (
      !game.completedAt ||
      playerStats.completedPuzzleIds.includes(game.puzzleId)
    )
      return

    const elapsedSeconds = Math.max(
      0,
      Math.round(
        (Date.parse(game.completedAt) - Date.parse(game.startedAt)) / 1000
      )
    )
    const nextStats = recordPuzzleCompletion(playerStats, {
      puzzleId: game.puzzleId,
      mode: game.mode,
      solutionIndex: game.solutionIndex,
      mistakes: game.mistakes,
      hintsUsed: game.hintsUsed,
      elapsedSeconds,
    })
    if (!savePlayerStats(nextStats)) return
    setPlayerStats(nextStats)
    setGame((previous) =>
      previous.puzzleId === game.puzzleId
        ? { ...previous, statsRecorded: true }
        : previous
    )
  }, [game, playerStats])

  const selectCell = useCallback(
    (row: number, col: number) => {
      const current = gameRef.current
      if (current.solvedRows[row]) return
      if (isCellEditable(current, row, col)) {
        setSelectedCell({ row, col })
        // Keep this focus call inside the tap event. Mobile Safari only opens
        // the native keyboard for programmatic focus during a user gesture.
        focusNativeInput()
        return
      }
      const editable = getEditableCellsForRow(
        row,
        SIZE,
        new Set(current.clueCells),
        new Set(current.revealedCells),
        getSolvedIndexes(current.solvedRows)
      )
      if (editable[0]) {
        setSelectedCell(editable[0])
        focusNativeInput()
      }
    },
    [focusNativeInput]
  )

  const moveSelection = useCallback(
    (direction: 1 | -1) => {
      const current = gameRef.current
      const editable = getEditableCellsForRow(
        selectedCell.row,
        SIZE,
        new Set(current.clueCells),
        new Set(current.revealedCells),
        getSolvedIndexes(current.solvedRows)
      )
      const position = editable.findIndex(
        (cell) => cell.row === selectedCell.row && cell.col === selectedCell.col
      )
      const next = editable[position + direction]
      if (next) {
        setSelectedCell(next)
        window.requestAnimationFrame(() => {
          selectedCellButtonRef.current?.scrollIntoView?.({ block: 'nearest' })
        })
      }
    },
    [selectedCell]
  )

  const moveRowSelection = useCallback(
    (direction: 1 | -1) => {
      const current = gameRef.current
      for (
        let row = selectedCell.row + direction;
        row >= 0 && row < SIZE;
        row += direction
      ) {
        const editable = getEditableCellsForRow(
          row,
          SIZE,
          new Set(current.clueCells),
          new Set(current.revealedCells),
          getSolvedIndexes(current.solvedRows)
        )
        const next = editable.reduce<CellPosition | null>((closest, cell) => {
          if (!closest) return cell
          return Math.abs(cell.col - selectedCell.col) <
            Math.abs(closest.col - selectedCell.col)
            ? cell
            : closest
        }, null)
        if (next) {
          setSelectedCell(next)
          window.requestAnimationFrame(() => {
            selectedCellButtonRef.current?.scrollIntoView?.({
              block: 'nearest',
            })
          })
          return
        }
      }
    },
    [selectedCell]
  )

  const onChar = useCallback(
    (value: string) => {
      const current = gameRef.current
      if (
        current.completedAt ||
        !isCellEditable(current, selectedCell.row, selectedCell.col)
      )
        return

      setGame((previous) => {
        const board = previous.board.map((row) => [...row])
        board[selectedCell.row][selectedCell.col] = value.toUpperCase()
        const lastAttempts = [...previous.lastAttempts]
        lastAttempts[selectedCell.row] = null
        return { ...previous, board, lastAttempts }
      })
      moveSelection(1)
    },
    [moveSelection, selectedCell]
  )

  const onDelete = useCallback(() => {
    const current = gameRef.current
    if (current.completedAt) return
    const { row, col } = selectedCell
    if (isCellEditable(current, row, col) && current.board[row][col]) {
      setGame((previous) => {
        const board = previous.board.map((line) => [...line])
        board[row][col] = ''
        const lastAttempts = [...previous.lastAttempts]
        lastAttempts[row] = null
        return { ...previous, board, lastAttempts }
      })
      return
    }

    const editable = getEditableCellsForRow(
      row,
      SIZE,
      new Set(current.clueCells),
      new Set(current.revealedCells),
      getSolvedIndexes(current.solvedRows)
    )
    const prior = [...editable].reverse().find((cell) => cell.col < col)
    if (prior) {
      setSelectedCell(prior)
      setGame((previous) => {
        const board = previous.board.map((line) => [...line])
        board[prior.row][prior.col] = ''
        const lastAttempts = [...previous.lastAttempts]
        lastAttempts[prior.row] = null
        return { ...previous, board, lastAttempts }
      })
    }
  }, [selectedCell])

  const onEnter = useCallback(() => {
    const current = gameRef.current
    if (current.completedAt || isChecking) return
    const row = current.solvedRows[selectedCell.row]
      ? current.solvedRows.findIndex((solved) => !solved)
      : selectedCell.row
    if (row < 0) return
    const guess = current.board[row].join('')
    if (guess.length !== SIZE || current.board[row].some((letter) => !letter)) {
      return showError(`Row ${row + 1} still has empty cells.`)
    }
    if (!isWordInWordList(guess)) {
      return showError(
        'That word is not in the list. Edit the row and try another.'
      )
    }
    if (current.lastAttempts[row] === guess) {
      return showError('Edit a letter before checking this row again.')
    }

    setIsChecking(true)
    window.setTimeout(() => setIsChecking(false), 420)
    const correct = guess === current.solution[row]
    setGame((previous) => {
      const attempts = [...previous.attempts]
      attempts[row] += 1
      const lastAttempts = [...previous.lastAttempts]
      lastAttempts[row] = guess
      if (!correct) {
        return {
          ...previous,
          attempts,
          lastAttempts,
          mistakes: previous.mistakes + 1,
        }
      }

      const solvedRows = [...previous.solvedRows]
      solvedRows[row] = true
      const completedAt = solvedRows.every(Boolean)
        ? new Date().toISOString()
        : null
      const board = applyKnownCells(
        previous.board,
        previous.solution,
        new Set(previous.clueCells),
        new Set(previous.revealedCells),
        getSolvedIndexes(solvedRows)
      )
      // Crossing letters have changed, so old feedback for other unfinished
      // rows no longer describes what is visibly on the board.
      lastAttempts.forEach((_, index) => {
        if (index !== row && !solvedRows[index]) lastAttempts[index] = null
      })
      return {
        ...previous,
        attempts,
        lastAttempts,
        solvedRows,
        board,
        completedAt,
      }
    })

    if (!correct) {
      showError('Not quite. Edit the row and try again.')
      return
    }
    showSuccess(`Row ${row + 1} solved.`)
    const nextRow = current.solvedRows.findIndex(
      (solved, index) => !solved && index !== row
    )
    if (nextRow >= 0) {
      const futureSolved = [...current.solvedRows]
      futureSolved[row] = true
      const editable = getEditableCellsForRow(
        nextRow,
        SIZE,
        new Set(current.clueCells),
        new Set(current.revealedCells),
        getSolvedIndexes(futureSolved)
      )
      setSelectedCell(editable[0] || { row: nextRow, col: 0 })
    }
  }, [isChecking, selectedCell.row, showError, showSuccess])

  const onNativeInputChange = useCallback(
    (value: string) => {
      const letter = localeAwareUpperCase(value).match(/[A-Z]/)?.[0]
      if (letter) onChar(letter)
    },
    [onChar]
  )

  const onNativeInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return

      if (event.key === 'Enter') {
        event.preventDefault()
        onEnter()
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()
        onDelete()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        moveSelection(-1)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        moveSelection(1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveRowSelection(-1)
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveRowSelection(1)
      } else if (event.key === 'Escape') {
        nativeInputRef.current?.blur()
      }
    },
    [moveRowSelection, moveSelection, onDelete, onEnter]
  )

  useEffect(() => {
    const input = nativeInputRef.current
    if (!input || isComplete) return

    const handleBeforeInput = (event: InputEvent) => {
      if (event.inputType?.startsWith('delete')) {
        event.preventDefault()
        onDelete()
      }
    }

    // React 17 does not expose native deletion inputTypes through its
    // synthetic onBeforeInput event. Listening on the actual input keeps
    // Backspace working on mobile keyboards that do not emit keydown.
    input.addEventListener('beforeinput', handleBeforeInput)
    return () => input.removeEventListener('beforeinput', handleBeforeInput)
  }, [isComplete, onDelete])

  const revealHint = () => {
    if (isComplete) return
    const current = gameRef.current
    let row = selectedCell.row
    if (current.solvedRows[row])
      row = current.solvedRows.findIndex((value) => !value)
    const editable = getEditableCellsForRow(
      row,
      SIZE,
      new Set(current.clueCells),
      new Set(current.revealedCells),
      getSolvedIndexes(current.solvedRows)
    )
    const targets = chooseHintCells({
      board: current.board,
      solution: current.solution,
      primaryCandidates: editable,
      selectedCell: selectedCell.row === row ? selectedCell : undefined,
      isEditable: (cell) => isCellEditable(current, cell.row, cell.col),
    })
    if (targets.length === 0) {
      return showError('This row has no useful hint left.')
    }

    const targetKeys = new Set(targets.map(cellKey))
    setGame((previous) => {
      const revealed = new Set(previous.revealedCells)
      targetKeys.forEach((key) => revealed.add(key))
      const board = applyKnownCells(
        previous.board,
        previous.solution,
        new Set(previous.clueCells),
        revealed,
        getSolvedIndexes(previous.solvedRows)
      )
      const lastAttempts = [...previous.lastAttempts]
      targets.forEach((target) => {
        lastAttempts[target.row] = null
      })
      return {
        ...previous,
        board,
        lastAttempts,
        revealedCells: Array.from(revealed),
        hintsUsed: previous.hintsUsed + 1,
      }
    })
    const remaining = editable.filter((cell) => !targetKeys.has(cellKey(cell)))
    if (remaining[0]) setSelectedCell(remaining[0])
    showSuccess(
      targets.length > 1 ? 'Matching pair revealed.' : 'Hint revealed.'
    )
  }

  const startDaily = () => {
    const next = restoreGame('daily', dailySolutionIndex, dailyPuzzleId)
    setGame(next)
    setSelectedCell(getFirstSelectableCell(next))
    announcedPuzzleRef.current = next.completedAt ? next.puzzleId : null
    setIsStatsOpen(false)
  }

  const startPractice = (forceNew = false) => {
    if (forceNew) {
      const previousPractice = loadCurrentPractice()
      if (previousPractice) clearGameState(previousPractice.puzzleId)
    }
    const storedPractice = !forceNew ? loadCurrentPractice() : null
    const existing =
      storedPractice &&
      storedPractice.solutionIndex >= 0 &&
      storedPractice.solutionIndex < SOLUTIONS.length &&
      storedPractice.solutionIndex !== dailySolutionCatalogIndex
        ? storedPractice
        : null
    let index =
      existing && existing.solutionIndex < SOLUTIONS.length
        ? existing.solutionIndex
        : choosePracticePuzzleIndexFromPool(
            CURATED_SOLUTION_INDEXES_V1,
            game.solutionIndex,
            [...getRecentPractice(), dailySolutionCatalogIndex]
          )
    if (index < 0) index = CURATED_SOLUTION_INDEXES_V1[0]
    const puzzleId = existing?.puzzleId || `practice-${index}-${Date.now()}`
    rememberPractice(index)
    const next = restoreGame('practice', index, puzzleId)
    setGame(next)
    setSelectedCell(getFirstSelectableCell(next))
    announcedPuzzleRef.current = next.completedAt ? next.puzzleId : null
    setIsStatsOpen(false)
    showSuccess(forceNew ? 'New board ready.' : 'Practice board loaded.')
  }

  const shareResult = async () => {
    const marks = game.solvedRows.map((solved, row) =>
      solved
        ? '🟩'.repeat(SIZE)
        : feedback[row]
            .map((status) =>
              status === 'correct'
                ? '🟩'
                : status === 'present'
                ? '🟨'
                : status
                ? '⬜'
                : '⬛'
            )
            .join('')
    )
    const text = `Word Square ${
      game.mode === 'daily' ? `Daily #${game.solutionIndex}` : 'Practice'
    }\n${marks.join('\n')}\n${solvedCount}/5 rows • ${
      game.mistakes
    } mistakes • ${game.hintsUsed} hints`
    try {
      if (navigator.share) {
        await navigator.share({ text })
        showSuccess('Result shared!')
      } else {
        await copyTextToClipboard(text)
        showSuccess('Result copied to your clipboard.')
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      try {
        await copyTextToClipboard(text)
        showSuccess('Result copied to your clipboard.')
      } catch {
        showError('Sharing is not available in this browser.')
      }
    }
  }

  const modeLabel =
    game.mode === 'daily' ? `Daily no. ${game.solutionIndex}` : 'Practice board'

  return (
    <div className="game-viewport">
      <div
        className="game-shell"
        data-layout="fixed-viewport"
        data-theme="classic-tiles"
      >
        <header className="game-header">
          <button
            className="icon-button"
            onClick={() => setIsInfoOpen(true)}
            aria-label="How to play"
          >
            <InformationCircleIcon />
          </button>
          <div className="brand-lockup">
            <h1>Word Square</h1>
          </div>
          <button
            className="icon-button"
            onClick={() => setIsStatsOpen(true)}
            aria-label="View progress"
          >
            <ChartBarIcon />
          </button>
        </header>

        <main className="game-main">
          <section
            className="game-card"
            aria-labelledby="puzzle-title"
            key={game.puzzleId}
          >
            <div className="game-toolbar">
              <h2 id="puzzle-title" className="board-title">
                <span className={`mode-dot mode-dot--${game.mode}`} />
                {modeLabel}
              </h2>
              <div className="toolbar-actions">
                {game.mode === 'daily' ? (
                  <button
                    className="text-button"
                    onClick={() => startPractice(false)}
                  >
                    Practice
                  </button>
                ) : (
                  <>
                    <button className="text-button" onClick={startDaily}>
                      <ClockIcon />
                      Daily
                    </button>
                    <button
                      className="text-button"
                      onClick={() => startPractice(true)}
                    >
                      <RefreshIcon />
                      New board
                    </button>
                  </>
                )}
              </div>
            </div>

            <div
              className="progress-track"
              role="progressbar"
              aria-label={`${solvedCount} of 5 rows solved`}
              aria-valuemin={0}
              aria-valuemax={SIZE}
              aria-valuenow={solvedCount}
            >
              <span style={{ width: `${(solvedCount / SIZE) * 100}%` }} />
            </div>
            <div className="puzzle-copy sr-only">
              <p aria-live="polite">
                {isComplete
                  ? 'Perfect square complete!'
                  : `Row ${activeRow + 1} · Correct rows reveal crossings.`}
              </p>
              <span className="sr-only">
                {solvedCount} of {SIZE} rows solved
              </span>
            </div>

            <PuzzleBoard
              solution={game.solution}
              board={game.board}
              solvedRows={game.solvedRows}
              attempts={game.attempts}
              clueCells={clueCells}
              revealedCells={revealedCells}
              feedback={feedback}
              selectedCell={selectedCell}
              onSelect={selectCell}
              selectedCellRef={selectedCellButtonRef}
            />

            {!isComplete && (
              <input
                ref={nativeInputRef}
                className="native-letter-input"
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                autoComplete="off"
                enterKeyHint="done"
                spellCheck={false}
                maxLength={1}
                value=""
                aria-label="Type a letter"
                aria-describedby="native-input-help"
                data-row={selectedCell.row}
                data-col={selectedCell.col}
                readOnly={isInfoOpen || isStatsOpen}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  event.currentTarget.value = ''
                  onNativeInputChange(value)
                }}
                onKeyDown={onNativeInputKeyDown}
              />
            )}

            {!isComplete && (
              <p className="native-input-help sr-only" id="native-input-help">
                Tap a tile · Type · Return to check
              </p>
            )}

            {!isComplete ? (
              <>
                <div className="action-row">
                  <button
                    className="hint-button"
                    onClick={revealHint}
                    aria-label="Reveal a hint"
                  >
                    <LightBulbIcon />
                    Hint
                  </button>
                  <button
                    className="check-button"
                    onClick={onEnter}
                    disabled={isChecking}
                  >
                    <CheckCircleIcon />
                    Check row {activeRow + 1}
                  </button>
                </div>
              </>
            ) : (
              <div className="completion-card">
                <CheckCircleIcon />
                <div>
                  <strong>Square complete!</strong>
                  <span>
                    {formatElapsed(game.startedAt, game.completedAt)} ·{' '}
                    {game.mistakes} mistakes · {game.hintsUsed} hints
                  </span>
                </div>
                <button onClick={shareResult}>
                  <ShareIcon />
                  Share
                </button>
                <button onClick={() => startPractice(true)}>
                  <RefreshIcon />
                  Play another
                </button>
              </div>
            )}

            {!isSaveAvailable && (
              <p className="autosave-note autosave-note--warning" role="alert">
                <ExclamationCircleIcon />
                Saving is unavailable in this browser
              </p>
            )}
          </section>
        </main>

        <InfoModal
          isOpen={isInfoOpen}
          handleClose={() => setIsInfoOpen(false)}
        />
        <ProgressModal
          isOpen={isStatsOpen}
          handleClose={() => setIsStatsOpen(false)}
          stats={playerStats}
          isComplete={isComplete}
          mode={game.mode}
          solvedRows={solvedCount}
          mistakes={game.mistakes}
          hintsUsed={game.hintsUsed}
          elapsed={formatElapsed(game.startedAt, game.completedAt)}
          currentDailyStreak={
            playerStats.lastDailyIndex !== null &&
            (dailySolutionIndex === playerStats.lastDailyIndex ||
              dailySolutionIndex === playerStats.lastDailyIndex + 1)
              ? playerStats.currentDailyStreak
              : 0
          }
          onShare={shareResult}
          onPlayAnother={() => startPractice(true)}
        />
        <AlertContainer />
      </div>
    </div>
  )
}

export default App
